<script setup lang="ts">
import { computed } from 'vue';
import { Timer } from '@element-plus/icons-vue';
import { usePerformanceStore } from '../../store/performance.store';

const props = defineProps<{
    page: string;
}>();

const perfStore = usePerformanceStore();

const entries = computed(() => perfStore.getPageLogs(props.page));
const totalMs = computed(() => perfStore.getPageTotalMs(props.page));

const typeColors: Record<string, string> = {
    api: '#409eff',
    mapping: '#e6a23c',
    display: '#67c23a',
    total: '#9c27b0',
};

const typeLabels: Record<string, string> = {
    api: 'API',
    mapping: 'Mappage',
    display: 'Affichage',
    total: 'Total',
};

const formatDuration = (ms: number) => {
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms}ms`;
};

const durationColor = (ms: number) => {
    if (ms < 500) return '#67c23a';
    if (ms < 2000) return '#e6a23c';
    return '#f56c6c';
};
</script>

<template>
    <el-popover
        v-if="perfStore.isEnabled"
        placement="bottom-end"
        :width="360"
        trigger="click"
        popper-class="perf-popover"
    >
        <template #reference>
            <div class="perf-badge" title="Performances">
                <el-icon :size="34"><Timer /></el-icon>
                <span v-if="entries.length > 0" class="perf-badge-count">{{ entries.length }}</span>
            </div>
        </template>

        <div class="perf-panel">
            <div class="perf-panel__header">
                <span class="perf-panel__title">
                    {{ page }}
                    <span
                        v-if="totalMs !== undefined"
                        class="perf-panel__total"
                        :style="{ color: durationColor(totalMs) }"
                    >{{ formatDuration(totalMs) }}</span>
                </span>
                <button class="perf-panel__clear" @click="perfStore.clearPage(page)">Effacer</button>
            </div>
            <div v-if="entries.length === 0" class="perf-panel__empty">Aucune mesure enregistrée</div>
            <div
                v-for="(entry, index) in entries"
                :key="index"
                class="perf-panel__entry"
            >
                <span class="perf-panel__type" :style="{ backgroundColor: typeColors[entry.type] }">
                    {{ typeLabels[entry.type] ?? entry.type }}
                </span>
                <span class="perf-panel__label" :title="entry.label">{{ entry.label }}</span>
                <span class="perf-panel__duration" :style="{ color: durationColor(entry.durationMs) }">
                    {{ formatDuration(entry.durationMs) }}
                </span>
            </div>
        </div>
    </el-popover>
</template>

<style scoped>
.perf-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #2B48A2;
}
.perf-badge-count {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #2B48A2;
    color: #fff;
    border-radius: 10px;
    padding: 0 5px;
    font-size: 11px;
    line-height: 16px;
    min-width: 16px;
    text-align: center;
}

.perf-panel {
    max-height: 420px;
    overflow-y: auto;
}
.perf-panel__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
}
.perf-panel__title {
    font-weight: 600;
    font-size: 13px;
    color: #303133;
    display: flex;
    align-items: center;
    gap: 8px;
}
.perf-panel__total {
    font-size: 14px;
    font-weight: 700;
}
.perf-panel__clear {
    background: none;
    border: none;
    cursor: pointer;
    color: #909399;
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 3px;
}
.perf-panel__clear:hover {
    color: #f56c6c;
    background: #fef0f0;
}
.perf-panel__empty {
    text-align: center;
    color: #909399;
    font-size: 12px;
    padding: 16px 0;
}
.perf-panel__entry {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 12px;
    border-bottom: 1px solid #f5f5f5;
}
.perf-panel__entry:last-child {
    border-bottom: none;
}
.perf-panel__type {
    flex-shrink: 0;
    width: 58px;
    text-align: center;
    color: #fff;
    border-radius: 3px;
    padding: 1px 0;
    font-size: 11px;
}
.perf-panel__label {
    flex: 1;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.perf-panel__duration {
    flex-shrink: 0;
    font-weight: 600;
    min-width: 55px;
    text-align: right;
    font-size: 12px;
}
</style>
